/**
 * EmployeeStatsCards Component
 * Statistics cards for employee dashboard
 */

import React from 'react';
import { Grid, Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import {
  People,
  CheckCircle,
  Block,
  MedicalServices,
  PersonOff,
  TrendingUp
} from '@mui/icons-material';
import { EmployeeStats, EmployeeStatus } from '../../types/employee';

interface EmployeeStatTileProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

/**
 * NÃO é o `StatCard` de `components/common/DetailView` — aquele é o tile compacto
 * da tela de visualização (ícone 40px, valor em `body2`). Este é o tile grande de
 * estatística da LISTAGEM: ícone 56px, número em `h4`, cor por palette key.
 */
const EmployeeStatTile: React.FC<EmployeeStatTileProps> = ({ title, value, icon, color, subtitle }) => (
  <Card>
    <CardContent>
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            bgcolor: `${color}.100`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: `${color}.700`
          }}
        >
          {icon}
        </Box>
        <Box flexGrow={1}>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

interface EmployeeStatsCardsProps {
  stats: EmployeeStats | null;
  loading?: boolean;
}

export const EmployeeStatsCards: React.FC<EmployeeStatsCardsProps> = ({
  stats,
  loading = false
}) => {
  if (loading) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Grid
            key={i}
            size={{
              xs: 12,
              sm: 6,
              md: 4,
              lg: 2
            }}>
            <Card>
              <CardContent>
                <Skeleton variant="rectangular" height={56} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!stats) {
    return null;
  }

  const calculatePercentage = (value: number, total: number): string => {
    if (total === 0) return '0%';
    return `${((value / total) * 100).toFixed(1)}%`;
  };

  return (
    <Grid container spacing={3}>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          md: 4,
          lg: 2
        }}>
        <EmployeeStatTile
          title="Total de Funcionários"
          value={stats.total}
          icon={<People fontSize="large" />}
          color="primary"
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          md: 4,
          lg: 2
        }}>
        <EmployeeStatTile
          title="Ativos"
          value={stats?.byStatus?.[EmployeeStatus.ATIVO] || 0}
          icon={<CheckCircle fontSize="large" />}
          color="success"
          subtitle={calculatePercentage(stats?.byStatus?.[EmployeeStatus.ATIVO] || 0, stats.total)}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          md: 4,
          lg: 2
        }}>
        <EmployeeStatTile
          title="Suspensos"
          value={stats?.byStatus?.[EmployeeStatus.SUSPENSO] || 0}
          icon={<Block fontSize="large" />}
          color="warning"
          subtitle={calculatePercentage(stats?.byStatus?.[EmployeeStatus.SUSPENSO] || 0, stats.total)}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          md: 4,
          lg: 2
        }}>
        <EmployeeStatTile
          title="Afastados"
          value={stats?.byStatus?.[EmployeeStatus.AFASTADO] || 0}
          icon={<MedicalServices fontSize="large" />}
          color="info"
          subtitle={calculatePercentage(stats?.byStatus?.[EmployeeStatus.AFASTADO] || 0, stats.total)}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          md: 4,
          lg: 2
        }}>
        <EmployeeStatTile
          title="Demitidos"
          value={stats?.byStatus?.[EmployeeStatus.DEMITIDO] || 0}
          icon={<PersonOff fontSize="large" />}
          color="error"
          subtitle={calculatePercentage(stats?.byStatus?.[EmployeeStatus.DEMITIDO] || 0, stats.total)}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          md: 4,
          lg: 2
        }}>
        <EmployeeStatTile
          title="PCD"
          value={stats.pcdCount}
          icon={<TrendingUp fontSize="large" />}
          color="secondary"
          subtitle="Portadores de deficiência"
        />
      </Grid>
    </Grid>
  );
};
